import { z } from 'zod';
import { axisSchema, boardSchema } from './dataset.js';
import { answerChoice, fitChoice, forcedChoice, naturalChoice, poleChoice, } from './questions.js';
/** What the reviewer's link resolves to. */
export const inviteSchema = z.object({
    token: z.string(),
    nick: z.string(),
    displayName: z.string().nullable(),
    title: z.string().nullable(),
    dataset: z.object({
        slug: z.string(),
        label: z.string(),
        revision: z.string(),
        band: z.string(),
        games: z.number().int(),
        decisions: z.number().int(),
        testNote: z.string(),
        reviewerQuestions: z.array(z.string()),
        totals: z.object({
            axes: z.number().int(),
            boards: z.number().int(),
        }),
    }),
    progress: z.object({
        answered: z.number().int(),
        total: z.number().int(),
        cursor: z.number().int(),
    }),
    submittedAt: z.string().nullable(),
});
export const reportSchema = z.object({
    axes: z.array(axisSchema),
});
/**
 * Which wording a reviewer preferred when a board carried two.
 *
 * The values name the authored variants, not the slots they appeared in:
 * the order on screen is shuffled per board, so "a" always means the same
 * text whichever side of the screen it landed on.
 */
export const betterVariant = z.enum(['a', 'b', 'both', 'neither']);
export const boardAnswerSchema = z.object({
    /**
     * The verdict on the board's commentary. With one wording it is that
     * wording's verdict; with two it is filled per variant below and this holds
     * the best of them, which is what the report would ship.
     */
    accuracy: answerChoice.nullish(),
    /**
     * Verdict per variant, keyed by variant name. A null value means the
     * reviewer cleared that verdict, and has to travel: leaving the key out
     * instead is indistinguishable from "not touched in this patch", so the
     * stored verdict would survive the clearing and come back on reload.
     */
    variants: z.record(z.string(), answerChoice.nullable()).nullish(),
    better: betterVariant.nullish(),
    forced: forcedChoice.nullish(),
    altNatural: naturalChoice.nullish(),
    polePlacement: poleChoice.nullish(),
    comment: z.string().max(4000).nullish(),
});
export const axisAnswerSchema = z.object({
    poleNameFits: fitChoice.nullish(),
    poleNamePrecise: fitChoice.nullish(),
    phaseCaveat: fitChoice.nullish(),
    comment: z.string().max(4000).nullish(),
});
export const surveySchema = z.object({
    exercises: fitChoice.nullish(),
    exclusions: z.string().max(8000).nullish(),
    general: z.string().max(8000).nullish(),
});
export const answersSchema = z.object({
    boards: z.record(z.string(), boardAnswerSchema),
    axes: z.record(z.string(), axisAnswerSchema),
    survey: surveySchema.nullable(),
    cursor: z.number().int(),
});
/* ------------------------------------------------------------------ admin */
export const verdictKind = z.enum(['keep', 'rewrite', 'drop']);
/**
 * A reviewer is a nick and nothing else (owner, 2026-08-03).
 *
 * Name, title and rating were collected and never used: the reviewer sees the
 * nick in their own header, and the admin recognises them by it in the
 * feedback table. Anything more is a field to keep up to date for no reader.
 */
export const reviewerInputSchema = z.object({
    nick: z.string().min(1).max(64),
});
/**
 * What the admin may rename after an import.
 *
 * `label` is the name reviewers read in their header, so it is editable
 * without re-importing: the exporter guesses initials, and the owner may want
 * something else there once the link is going to a person.
 */
export const datasetUpdateSchema = z.object({
    label: z.string().min(1).max(120).optional(),
    revision: z.string().max(120).optional(),
});
export const inviteInputSchema = z.object({
    datasetSlug: z.string(),
    reviewerId: z.string(),
});
export const verdictInputSchema = z.object({
    boardId: z.string(),
    verdict: verdictKind,
    note: z.string().max(2000).nullish(),
});
/** One row of the admin feedback table. */
export const feedbackRowSchema = z.object({
    board: boardSchema,
    axisKey: z.string(),
    axisOrdinal: z.number().int(),
    axisTitle: z.string(),
    counts: z.object({
        trafny: z.number().int(),
        czesciowo: z.number().int(),
        nietrafny: z.number().int(),
        answers: z.number().int(),
    }),
    variants: z.array(z.object({
        variant: z.string(),
        generator: z.string().nullable(),
        trafny: z.number().int(),
        czesciowo: z.number().int(),
        nietrafny: z.number().int(),
        preferred: z.number().int(),
    })),
    verdict: verdictKind.nullable(),
    verdictNote: z.string().nullable(),
    suggestedRating: z.number().int().nullable(),
    responses: z.array(z.object({
        nick: z.string(),
        title: z.string().nullable(),
        answer: boardAnswerSchema,
        updatedAt: z.string(),
    })),
});
//# sourceMappingURL=api.js.map